import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Typography } from 'antd';

import EventFormElement from '../events/EventFormElement';
import { getElementValidationErrors } from '../../utils/elements';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { canWriteCard } from '../../utils/permissions';
import { lockStateFor, recordPolicyFor } from '../../utils/recordPolicy';
import { formatCount } from '../../utils/counts';
import { isSeeOnlyElement } from '../../utils/elementAccess';
import {
	applyToIds,
	bulkApplyToFilter,
	dryRunIds,
	newBatchId,
	rowErrorList,
} from '../../services/itemsBulk';

const { Text, Paragraph } = Typography;

/**
 * "Apply to selected": set one element, or delete — or,
 * for intake submissions waiting for review, accept or reject them.
 *
 * The design constraints, in the order they matter:
 *
 * - **Validation is all-or-nothing.** One refused row refuses the whole
 *   apply. So every refusal the client can know about is shown BEFORE the
 *   commit button lights — record locks and per-card write permission are
 *   computed here from the same rules the API runs (`utils/recordPolicy`,
 *   `utils/permissions`) — and the panel offers to drop those rows from the
 *   selection rather than leaving the user to discover a 400.
 * - **Dry run, then commit.** The check is a real request (the server knows
 *   things this side cannot: another user's lock, a card's attestation, a
 *   required element the row would empty), and it writes nothing. A
 *   selection larger than one request is dry-run CHUNK BY CHUNK and only
 *   committed when every chunk came back clean.
 * - **Chunking breaks all-or-nothing across chunks**, and the panel says so
 *   in those words rather than reporting a clean success it cannot promise.
 * - **Setting a value needs one card.** An element belongs to a card; a
 *   selection spanning three cards has no shared element to set, and the
 *   same `uniqueCardId` gate the table's element columns and the tile editor
 *   already use decides it.
 * - **Triage is the same gesture** (`op: 'accept' | 'reject'`, the API's
 *   G9 bulk ops). Accepting makes each submission an item like any other and
 *   cannot be undone from History; rejecting deletes, restorable from History
 *   back into the queue. A row that is not waiting for review is a
 *   `not_pending` refusal, shown against its row like any other. The record
 *   lock is not asked of a triage row (a submission nobody accepted is not a
 *   record yet), so a locked-looking row does not block one.
 */
const TRIAGE_MODES = ['accept', 'reject'];

export default function ItemsApplyPanel({
	open,
	mode,                 // 'set' | 'delete' | 'accept' | 'reject'
	onClose,
	collectionId,
	collection = null,
	cardsById = {},
	getElementsForCard,
	items = [],           // the selected items the feed holds
	ids = [],             // the selected ids (empty under allMatching)
	allMatching = false,
	matchingCount = 0,
	excluded = [],        // ids subtracted from an allMatching selection
	filter = null,        // { card_id, include_descendants, q } — the list's own
	resolveLookupValue,
	onApplied,
	onDropIds,
}) {
	const [elementName, setElementName] = useState(null);
	const [value, setValue] = useState(undefined);
	const [reason, setReason] = useState('');
	const [plan, setPlan] = useState(null);
	const [refusals, setRefusals] = useState([]);
	const [failure, setFailure] = useState(null);
	const [busy, setBusy] = useState(null);       // 'checking' | 'saving'

	const count = allMatching ? Math.max(matchingCount - excluded.length, 0) : ids.length;

	// The `select` this panel sends. `expectedCount` stays the FILTER's own
	// total — the number the list showed — and anything the user dropped
	// rides as `exclude`, so the 409 count check keeps meaning what it says
	// and the client does no arithmetic to stay agreeable with the server.
	const select = useMemo(() => ({
		...filter,
		expectedCount: matchingCount,
		...(excluded.length ? { exclude: excluded } : {}),
	}), [filter, matchingCount, excluded]);

	// Any change to what is being applied invalidates the plan: a commit
	// button lit by a check of something else is the worst button on the page.
	const resetPlan = useCallback(() => { setPlan(null); setRefusals([]); setFailure(null); }, []);
	useEffect(() => { resetPlan(); }, [mode, elementName, value, reason, ids.length, allMatching, excluded.length, resetPlan]);
	useEffect(() => {
		if (open) return;
		setElementName(null); setValue(undefined);
		setReason(''); resetPlan(); setBusy(null);
	}, [open, resetPlan]);

	// ---- the shared card, and the elements it offers ----

	const uniqueCardId = useMemo(() => {
		if (allMatching) {
			// The filter names one card and does not sweep its children in:
			// that IS one card. Anything else is a set we cannot name an
			// element of without asking the server for every row.
			return filter?.card_id && !filter?.include_descendants ? filter.card_id : null;
		}
		const set = new Set((items || []).map(item => normalizeId(item?.card_id)).filter(Boolean));
		return set.size === 1 ? Array.from(set)[0] : null;
	}, [allMatching, filter, items]);

	const elementDefs = useMemo(() => {
		if (!uniqueCardId || typeof getElementsForCard !== 'function') return [];
		// Never a file element (PR 10b): bulk edit cannot set or clear a file
		// (the API refuses one, 400 file_not_settable). Nor a "Me too" count
		// (KPR-390): only the people who confirm move it (the API refuses a
		// row that sets one, confirmations_not_settable). Nor an element a rule
		// holds this person to "see only" (KPR-509): the API refuses the whole
		// batch for one (`element_read_only`, reason `grant`).
		return (getElementsForCard(uniqueCardId) || []).filter(def => def?.name && !def?.options?.drivenFrom && def.dataType !== 'file' && def.dataType !== 'confirmations' && !isSeeOnlyElement(def));
	}, [uniqueCardId, getElementsForCard]);

	const element = useMemo(
		() => elementDefs.find(def => def.name === elementName) || null,
		[elementDefs, elementName]
	);

	// ---- what this side already knows will be refused ----

	const triage = TRIAGE_MODES.includes(mode);
	const blocked = useMemo(() => {
		if (allMatching) return { locked: [], forbidden: [] };
		const locked = [];
		const forbidden = [];
		(items || []).forEach((item) => {
			const cardId = normalizeId(item?.card_id);
			if (collection && !canWriteCard(collection, cardId)) { forbidden.push(item); return; }
			if (!triage && lockStateFor(item, recordPolicyFor(cardsById, cardId)).locked) locked.push(item);
		});
		return { locked, forbidden };
	}, [allMatching, items, collection, cardsById, triage]);

	const blockedIds = useMemo(
		() => [...blocked.locked, ...blocked.forbidden].map(item => normalizeId(item._id)),
		[blocked]
	);

	const titleOf = useCallback((item) => getItemDisplayValue({
		card: cardsById?.[normalizeId(item?.card_id)] || null,
		elementDefs: typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) || [] : [],
		item,
		resolveLookupValue,
	}), [cardsById, getElementsForCard, resolveLookupValue]);

	// The refused ids the SERVER named, which the client could not have known
	// (another user's lock, a card's attestation, a required element the row
	// would empty).
	const refusedIds = useMemo(
		() => Array.from(new Set(refusals.map(row => row?._id).filter(Boolean))),
		[refusals]
	);

	const itemsById = useMemo(() => {
		const map = {};
		(items || []).forEach(item => { map[normalizeId(item?._id)] = item; });
		return map;
	}, [items]);

	// ---- the patch ----

	const validationError = useMemo(() => {
		if (mode !== 'set') return null;
		if (!element) return null;
		const errors = getElementValidationErrors([element], { [element.name]: value });
		return errors[element.name] || null;
	}, [mode, element, value]);

	const patch = useMemo(() => {
		if (mode === 'delete') return { op: 'delete' };
		if (mode === 'accept') return { op: 'accept' };
		// A reject row carries its own reason — the batch-level `reason` is
		// the delete rows' default, not a triage one.
		if (mode === 'reject') return reason.trim() ? { op: 'reject', reason: reason.trim() } : { op: 'reject' };
		if (!element) return null;
		return { elements: { [element.name]: value === undefined ? '' : value } };
	}, [mode, element, value, reason]);

	const ready = (mode === 'delete' || triage)
		? true
		: Boolean(element) && !validationError;

	// Memoized: it is a dependency of both requests below, and a fresh object
	// every render would rebuild them every render.
	const options = useMemo(
		() => (triage ? {} : { reason: reason.trim() || undefined }),
		[reason, triage]
	);

	// ---- check, then apply ----

	const runCheck = useCallback(async () => {
		if (!patch) return null;
		setBusy('checking');
		setFailure(null);
		try {
			if (allMatching) {
				const result = await bulkApplyToFilter(
					collectionId,
					select,
					patch,
					{ dryRun: true, ...options }
				);
				setPlan({ summary: result?.summary, matched: result?.matched, rows: result?.rows || [] });
				setRefusals([]);
				return result;
			}
			const result = await dryRunIds(collectionId, ids, patch, options);
			setPlan({ summary: result.summary, matched: ids.length, rows: result.rows });
			setRefusals(result.refusals);
			return result;
		} catch (error) {
			const rows = rowErrorList(error);
			setRefusals(rows);
			setPlan(null);
			if (!rows.length) setFailure(errorMessageOf(error));
			return null;
		} finally {
			setBusy(null);
		}
	}, [patch, allMatching, collectionId, select, ids, options]);

	const runApply = useCallback(async () => {
		if (!patch) return;
		setBusy('saving');
		setFailure(null);
		try {
			if (allMatching) {
				const result = await bulkApplyToFilter(
					collectionId,
					select,
					patch,
					{ batchId: newBatchId(), ...options }
				);
				onApplied?.(result, { mode, chunks: 1 });
			} else {
				const result = await applyToIds(collectionId, ids, patch, options);
				onApplied?.(result, { mode, chunks: result.chunks });
			}
			onClose?.();
		} catch (error) {
			const rows = rowErrorList(error);
			setRefusals(rows);
			setPlan(null);
			setFailure(error?.partialBatch
				? `${error.partialBatch.written.length} of ${count} items were changed before this failed. A selection this large is sent in batches, so it is NOT all-or-nothing across them.`
				: errorMessageOf(error));
		} finally {
			setBusy(null);
		}
	}, [patch, allMatching, collectionId, select, ids, options, onApplied, onClose, mode, count]);

	// ---- render ----

	const chunks = Math.ceil((allMatching ? 0 : ids.length) / 200);
	const titleFor = {
		set: 'Set a value', delete: 'Delete items', accept: 'Accept submissions', reject: 'Reject submissions',
	}[mode] || 'Apply';
	const commitLabel = { delete: 'Delete', accept: 'Accept', reject: 'Reject' }[mode] || 'Apply';

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={`${titleFor} · ${formatCount(count, 'item') || 'no items'}`}
			destroyOnClose
			width={560}
			footer={[
				<Button key="cancel" onClick={onClose}>Cancel</Button>,
				<Button
					key="check"
					onClick={runCheck}
					loading={busy === 'checking'}
					disabled={!ready || busy === 'saving'}
					data-action="bulk-check"
				>
					Check
				</Button>,
				<Button
					key="apply"
					type="primary"
					danger={mode === 'delete' || mode === 'reject'}
					onClick={runApply}
					loading={busy === 'saving'}
					// The commit button lights only after a CLEAN check.
					// All-or-nothing means an unchecked apply is a coin flip
					// over the whole selection.
					disabled={!ready || !plan || refusals.length > 0 || busy === 'checking'}
					data-action="bulk-apply"
				>
					{commitLabel}
				</Button>,
			]}
		>
			{allMatching && excluded.length ? (
				<Alert
					type="info"
					showIcon
					className="items-apply__note"
					data-testid="items-apply-excluded"
					message={`${excluded.length} ${excluded.length === 1 ? 'item was' : 'items were'} removed from this selection`}
					description="The filter is unchanged — those rows are subtracted from it for this apply."
				/>
			) : null}

			{blockedIds.length ? (
				<Alert
					type="warning"
					showIcon
					className="items-apply__blocked"
					message={describeBlocked(blocked)}
					description={
						<>
							<Paragraph style={{ marginBottom: 8 }}>
								The whole apply is refused if any row is refused, so these have to
								leave the selection first.
							</Paragraph>
							<ul className="items-apply__list">
								{[...blocked.locked, ...blocked.forbidden].slice(0, 6).map(item => (
									<li key={normalizeId(item._id)}>{titleOf(item)}</li>
								))}
								{blockedIds.length > 6 ? <li>…and {blockedIds.length - 6} more</li> : null}
							</ul>
							<Button size="small" onClick={() => onDropIds?.(blockedIds)} data-action="drop-blocked">
								Remove them from the selection
							</Button>
						</>
					}
				/>
			) : null}

			{mode === 'set' ? (
				<div className="items-apply__field">
					{uniqueCardId ? (
						<>
							<Select
								className="items-apply__element"
								placeholder="Which field?"
								value={elementName}
								onChange={(name) => { setElementName(name); setValue(undefined); }}
								options={elementDefs.map(def => ({
									value: def.name,
									label: def?.label?.singular || def.name,
								}))}
								showSearch
								optionFilterProp="label"
								data-testid="items-apply-element"
							/>
							{element ? (
								<EventFormElement
									event={{ elements: { [element.name]: value } }}
									element={element}
									value={value}
									onChange={(name, next) => setValue(next)}
									cardMeta={cardsById?.[uniqueCardId] || null}
									showIconBadge={false}
									errorMessage={validationError}
									siblingDefs={elementDefs}
								/>
							) : null}
							<Text type="secondary" className="items-apply__note">
								Only this field changes. Everything else on each item is left alone.
							</Text>
						</>
					) : (
						<Alert
							type="info"
							showIcon
							message="These items do not share one card type"
							description="A field belongs to a card, so setting one needs a selection of a single card type. Filter the list by card type first."
						/>
					)}
				</div>
			) : null}

			{mode === 'delete' ? (
				<div className="items-apply__field">
					<Paragraph>
						{formatCount(count, 'item') || 'No items'} will be deleted. A deletion can be
						reverted from their history for 30 days, but the items leave the list now.
					</Paragraph>
					<label className="items-apply__label" htmlFor="items-apply-reason">Reason</label>
					<Input
						id="items-apply-reason"
						value={reason}
						onChange={event => setReason(event.target.value)}
						placeholder="Why these are being deleted"
						data-testid="items-apply-reason"
					/>
					<Text type="secondary" className="items-apply__note">
						Cards that keep records require one, and it is kept with the deletion.
					</Text>
				</div>
			) : null}

			{mode === 'accept' ? (
				<div className="items-apply__field">
					<Paragraph>
						{formatCount(count, 'submission') || 'No submissions'} will become items of this
						collection like any other: they join the list, counts and exports, and the
						collection’s automations run on each of them once.
					</Paragraph>
					<Text type="secondary" className="items-apply__note">
						Accepting cannot be undone from History. Check what they say first.
					</Text>
				</div>
			) : null}

			{mode === 'reject' ? (
				<div className="items-apply__field">
					<Paragraph>
						{formatCount(count, 'submission') || 'No submissions'} will be deleted. Each can be
						restored from History, which puts it back in the queue still waiting for review.
					</Paragraph>
					<label className="items-apply__label" htmlFor="items-apply-reject-reason">Reason (optional)</label>
					<Input
						id="items-apply-reject-reason"
						value={reason}
						// The API's reason cap (keepr-api itemCorrections MAX_REASON).
						maxLength={2000}
						onChange={event => setReason(event.target.value)}
						placeholder="e.g. Spam"
						data-testid="items-apply-reject-reason"
					/>
				</div>
			) : null}

			{chunks > 1 ? (
				<Alert
					type="info"
					showIcon
					className="items-apply__note"
					message={`This is sent in ${chunks} batches`}
					description="Every batch is checked before any of them is saved — but once saving starts, a failure part-way leaves the earlier batches written. The result will say how many landed."
				/>
			) : null}

			{failure ? <Alert type="error" showIcon message={failure} className="items-apply__note" /> : null}

			{refusals.length ? (
				<Alert
					type="error"
					showIcon
					className="items-apply__note"
					message={`${refusals.length} ${refusals.length === 1 ? 'item cannot' : 'items cannot'} be changed — nothing was saved`}
					description={
						<>
							<ul className="items-apply__list" data-testid="items-apply-refusals">
								{refusals.slice(0, 8).map((row, index) => {
									const item = row?._id ? itemsById[row._id] : null;
									return (
										<li key={`${row?._id || 'row'}-${index}`}>
											<Text strong>{item ? titleOf(item) : `Row ${(row?.index ?? 0) + 1}`}</Text>
											{' — '}
											{row?.message || row?.code}
										</li>
									);
								})}
								{refusals.length > 8 ? <li>…and {refusals.length - 8} more</li> : null}
							</ul>
							{/* All-or-nothing cuts both ways: the refusals are
							    also the only thing standing between the user
							    and the rest of their selection, so dropping
							    them has to be one click and not a hunt back
							    through the list. */}
							{/* Under `allMatching` this holds no ids to filter,
							    so the drop is a SUBTRACTION carried to the
							    server as `select.exclude`. It used to filter an
							    empty list and change nothing at all, which left
							    the user with a selection the server refused
							    identically forever. */}
							{refusedIds.length ? (
								<Button size="small" onClick={() => onDropIds?.(refusedIds)} data-action="drop-refused">
									Remove {refusedIds.length === 1 ? 'it' : `those ${refusedIds.length}`} from the selection
								</Button>
							) : null}
						</>
					}
				/>
			) : null}

			{plan && !refusals.length ? (
				<Alert
					type="success"
					showIcon
					className="items-apply__note"
					data-testid="items-apply-plan"
					message={describePlan(plan, mode)}
				/>
			) : null}
		</Modal>
	);
}

function describeBlocked({ locked, forbidden }) {
	const parts = [];
	if (locked.length) parts.push(`${locked.length} ${locked.length === 1 ? 'is a closed record' : 'are closed records'}`);
	if (forbidden.length) parts.push(`${forbidden.length} you cannot edit`);
	return `${locked.length + forbidden.length} of the selected items cannot be changed: ${parts.join(', ')}.`;
}

function describePlan(plan, mode) {
	const s = plan?.summary || {};
	if (mode === 'delete') return `${formatCount(s.delete, 'item') || 'Nothing'} will be deleted.`;
	if (mode === 'accept') return `${formatCount(s.accept, 'submission') || 'Nothing'} will be accepted.`;
	if (mode === 'reject') return `${formatCount(s.reject, 'submission') || 'Nothing'} will be rejected and deleted.`;
	const n = (s.update || 0) + (s.create || 0);
	return `${formatCount(n, 'item') || 'Nothing'} will be changed.`;
}

function errorMessageOf(error) {
	const data = error?.response?.data;
	if (data?.code === 'count_mismatch') {
		return `${data.message || 'The filter changed.'} Refresh the list and select again.`;
	}
	if (data?.code === 'filter_too_large') {
		return data.message || 'That filter matches too many items; narrow it.';
	}
	return data?.message || error?.message || 'That could not be applied.';
}
